import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import clinicService from '../../../../../services/clinic.service';
import { Link } from 'react-router-dom';
import Swal from '../../../../helpers/swal';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

function LinksCard({ linkitem, getLinks }) {
    const [edit, setEdit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: {
            id: linkitem.id,
            linkType: linkitem.linkType,
            clinicId: linkitem.clinicId,
            link: linkitem.link,
            notes: linkitem.notes,
            extraData: linkitem.extraData,
            isActive: linkitem.isActive.toString(),
        },
    });

    const onSubmit = (data) => {
        setIsSubmitting(true);
        clinicService
            .addUpdateReputationLink(linkitem.clinicId, data)
            .then(() => {
                toast.success('Link updated successfully!', { position: toast.POSITION.BOTTOM_RIGHT });
                setEdit(false);
                getLinks();
            })
            .catch((err) => {
                console.log(err);
                toast.error('Error updating link. Please try again.', { position: toast.POSITION.BOTTOM_RIGHT });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleDelete = (clinicId, id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `<i class="bi bi-check"></i> Yes, delete it!`,
            cancelButtonText: `<i class="bi bi-x"></i> Cancel`,
            reverseButtons: true,
            customClass: {
                confirmButton: 'btn btn-danger btn-sm me-2',
                cancelButton: 'btn btn-secondary btn-sm me-2',
            },
            buttonsStyling: false,
        }).then((result) => {
            if (result.isConfirmed) {
                clinicService
                    .deleteReputationLink(clinicId, id)
                    .then(() => {
                        getLinks();
                        Swal.fire({
                            title: 'Deleted!',
                            text: 'Link has been deleted successfully.',
                            icon: 'success',
                            timer: 1500,
                            showConfirmButton: false,
                        });
                    })
                    .catch(() => {
                        Swal.fire({
                            title: 'Error!',
                            text: 'Unable to delete link. Please try again.',
                            icon: 'error',
                        });
                    });
            }
        });
    };

    const getPlatformIconClass = (linkType) => {
        switch (linkType) {
            case 'FACEBOOK':
                return 'bi-facebook reputation-platform-icon--facebook';
            case 'INSTAGRAM':
                return 'bi-instagram reputation-platform-icon--instagram';
            case 'GOOGLE':
                return 'bi-google reputation-platform-icon--google';
            case 'YELP':
                return 'bi-star-fill reputation-platform-icon--yelp';
            case 'WEBSITE':
                return 'bi-globe reputation-platform-icon--website';
            default:
                return 'bi-link-45deg reputation-platform-icon--default';
        }
    };

    const cancelEdit = () => {
        setEdit(false);
        reset();
    };

    return (
        <div className="notifications-panel h-100">
            <div className="notifications-panel__header reputation-link-card__header">
                <span className="reputation-link-card__platform">
                    <i className={`bi ${getPlatformIconClass(linkitem.linkType)}`}></i>
                    {linkitem.linkType.toLowerCase()}
                </span>
                {!edit && (
                    <ActionTooltipButton
                        tooltip="Edit link"
                        tooltipId={`edit-link-${linkitem.id}`}
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setEdit(!edit)}
                    >
                        <i className="bi bi-pencil"></i>
                    </ActionTooltipButton>
                )}
            </div>

            <div className="notifications-panel__body">
                {!edit ? (
                    <>
                        <div className="reputation-link-card__url mb-2">
                            <Link to={linkitem.link} className="text-break" target="_blank" rel="noopener noreferrer">
                                {linkitem.link} <i className="bi bi-box-arrow-up-right"></i>
                            </Link>
                        </div>

                        {linkitem.notes && (
                            <div className="notifications-info-row mb-0">
                                <span className="notifications-info-label">Notes</span>
                                <span className="notifications-info-value">{linkitem.notes}</span>
                            </div>
                        )}

                        {linkitem.extraData && (
                            <div className="notifications-info-row mb-0">
                                <span className="notifications-info-label">Extra Data</span>
                                <span className="notifications-info-value">{linkitem.extraData}</span>
                            </div>
                        )}

                        <div className="reputation-link-card__footer">
                            <span className={`reputation-status-badge ${linkitem.isActive ? 'reputation-status-badge--active' : 'reputation-status-badge--inactive'}`}>
                                {linkitem.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <ActionTooltipButton
                                tooltip="Delete link"
                                tooltipId={`delete-link-${linkitem.id}`}
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(linkitem.clinicId, linkitem.id)}
                            >
                                <i className="bi bi-trash"></i>
                            </ActionTooltipButton>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-3">
                            <label htmlFor={`link-${linkitem.id}`} className="notifications-form-label">
                                URL
                            </label>
                            <input
                                type="text"
                                id={`link-${linkitem.id}`}
                                className={`form-control form-control-sm ${errors.link ? 'is-invalid' : ''}`}
                                {...register('link', { required: 'URL is required' })}
                            />
                            {errors.link && <div className="invalid-feedback">{errors.link.message}</div>}
                            <input type="hidden" {...register('id')} />
                            <input type="hidden" {...register('clinicId')} />
                            <input type="hidden" {...register('linkType')} />
                        </div>

                        <div className="mb-3">
                            <label htmlFor={`notes-${linkitem.id}`} className="notifications-form-label">
                                Notes
                            </label>
                            <input type="text" id={`notes-${linkitem.id}`} className="form-control form-control-sm" {...register('notes')} />
                        </div>

                        <div className="mb-3">
                            <label htmlFor={`extra-${linkitem.id}`} className="notifications-form-label">
                                Extra Data
                            </label>
                            <input type="text" id={`extra-${linkitem.id}`} className="form-control form-control-sm" {...register('extraData')} />
                        </div>

                        <div className="mb-3">
                            <label htmlFor={`status-${linkitem.id}`} className="notifications-form-label">
                                Status
                            </label>
                            <select id={`status-${linkitem.id}`} className="form-select form-select-sm" {...register('isActive')}>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>

                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-secondary btn-sm flex-fill" onClick={cancelEdit} disabled={isSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-success btn-sm flex-fill" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                ) : (
                                    <>
                                        <i className="bi bi-floppy me-1"></i>
                                        Update
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default LinksCard;
